import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum'
import { configureAuth } from '@lastbrand/fe-common/universal-auth'
import '@lastbrand/web-ui/styles.css'
import '@src/globals.css'
import '@src/SectionRenderer/sections/gridSlider/gridSlider.scss'
import '@src/quince-components/src/atoms/swiper/swiper.scss'
import '@src/quince-site-integrated/src/components/FormElements/inputStyles.scss'
import '@src/quince-site-integrated/src/components/FormElements/passwordStyles.scss'
import '@src/pages/contact-us/contact-us-components/glady-help-center/gladly-help-center-external.scss'
import '@src/quince-styles/scss/index.scss' // THIS IS INTENTIONAL DO NOT CHANGE THE ORDER!!!!! THIS SHOULD ALWAYS BE THE LAST IMPORT
import { AppProps } from 'next/app'
import Head from 'next/head'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { isMobile } from 'react-device-detect'

import { DD_RUM_APP_ID, DD_RUM_ENABLED_FLAG, DD_RUM_CLIENT_TOKEN, DD_RUM_SERVICE } from '@src/config/env'
import getAmplifyConfig from '@src/modules/auth/amplify/amplify-config'
import { initializeClientEntry } from '@src/modules/client-entry'
import { setUserAgnosticGlobalFeatureGatesInMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useOnRouteUpdateWithStateFlush } from '@src/quince-site-common/src/hooks/useOnRouteUpdateWithStateFlush'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { getCookie } from '@src/quince-site-integrated/src/utils'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { initializeFeCommon } from '@src/utils/feCommonInitialize'

import nextI18nextConfig from '../../next-i18next.config'

if (DD_RUM_ENABLED_FLAG && DD_RUM_APP_ID && DD_RUM_CLIENT_TOKEN) {
  const datadogConfig: RumInitConfiguration = {
    applicationId: DD_RUM_APP_ID,
    clientToken: DD_RUM_CLIENT_TOKEN,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: DD_RUM_SERVICE,
    env: process.env.NEXT_PUBLIC_DD_RUM_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    sessionSampleRate: 1,
    // All of those 1% sessions will include session replay
    sessionReplaySampleRate: 1,
    traceSampleRate: 10,
    telemetrySampleRate: 1,
    // @i18n-ignore
    defaultPrivacyLevel: 'mask-user-input',
    // Enable tracking of user interactions, resources, and long tasks (required for Clickmaps)
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // TODO: Uncomment this when we have a way to trigger session-replay programmatically, Now Session Replay starts recording automatically when calling init()
    // startSessionReplayRecordingManually: true, // Allow to do session-replay programmatically if needed
    allowedTracingUrls: [
      // @i18n-ignore
      { match: 'https://api-prod-public.onequince.com/', propagatorTypes: ['tracecontext'] },
      // @i18n-ignore
      { match: 'https://api-qa.onequince.com/', propagatorTypes: ['tracecontext'] },
    ],
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  datadogRum.init(datadogConfig)
  // console.log('Datadog initialized with configuration', datadogConfig)
}

if (isBrowser()) {
  const authConfig: Record<string, unknown> = {
    ...getAmplifyConfig(),
    onError(error: Error, context: Record<string, unknown>) {
      captureException(error, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        extraInfo: context,
      })
    },
  }

  // it initializes the auth provider with cognito config

  // using this we have to change tsconfig.json to node16
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  void configureAuth(authConfig)
}

initializeFeCommon()

function MyApp({ Component, pageProps }: AppProps<ServerSideBaseProps>) {
  const { t } = useTranslation('common')
  useOnRouteUpdateWithStateFlush([pdpStateManager], pageProps)
  setUserAgnosticGlobalFeatureGatesInMemory(pageProps.userAgnosticGlobalFeatureGates)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    initializeClientEntry(pageProps.userAgnosticGlobalFeatureGates)
    const isNewUserInner = getCookie('new-user-created') === 'random' && getCookie('_lb_user_first_access') === 'true'

    if (isNewUserInner) {
      trackEvent(TELEMETRY_EVENT_NAMES.NEW_USER_CREATED_RANDOM, {})
    }
  }, [pageProps.userAgnosticGlobalFeatureGates])

  return (
    <>
      <Head>
        <meta
          name="viewport"
          // @i18n-ignore
          content={`width=device-width, initial-scale=1${getIsNativeViewClient() || isMobile ? ', maximum-scale=1' : ''}`}
        />
      </Head>
      <nav aria-label={t('accessibility_link')}>
        <a className="sr-only" href="/accessibility">
          {t('view_our_accessibility_statement')}
        </a>
      </nav>
      <Component {...pageProps} />
    </>
  )
}

export default appWithTranslation(MyApp, nextI18nextConfig)
